import {
  ApplicationConfig,
  DEFAULT_CURRENCY_CODE,
  ErrorHandler,
  importProvidersFrom,
  inject,
  Injectable,
  LOCALE_ID,
  OnDestroy,
  provideAppInitializer,
  provideZoneChangeDetection,
} from "@angular/core";
import { provideRouter, Router, withComponentInputBinding, withRouterConfig } from "@angular/router";
import { provideAnimationsAsync } from "@angular/platform-browser/animations/async";
import { routes } from "./app.routes";
import { providePrimeNG } from "primeng/config";
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptors, withInterceptorsFromDi } from "@angular/common/http";
import { environment } from "@environments/environment";
import { initializeTranslation, provideTranslation } from "./kernel/translations/translation.config";
import { TranslationService } from "./kernel/translations/translation.service";
import { ELocalizationLang } from "./shared-core/enums/localization-lang.enum";
import { LocalStorageService } from "./kernel/tools/services/local-storage.service";
import { registerLocaleData } from "@angular/common";
import { provideMessage } from "./kernel/tools/message/message.config";
import { provideSideNav } from "./shared/general/sidenav/providers/sidenav";
import { provideGraphQL } from "./kernel/graphql/graphql.config";
import { provideConfirmationDialog } from "./kernel/popups/popup/modules/confirmation-dialog/confirmation-dialog.config";
import { JwtModule } from "@auth0/angular-jwt";
import { provideNotifications } from "./kernel/notifications/notification.config";
import { provideAlerts } from "./kernel/tools/alerts/alerts.config";
import { provideFirebase } from "./kernel/notifications/filrebase.config";
import { NotificationApiService } from "./shared/general/notifications/services/notification-api.service";
import { ErrorInterceptor } from "./shared/general/interceptors/error.interceptor";
import { AuthService } from "./shared/auth/services/auth.service";
import { AuthService as BaseAuthService } from "./kernel/tools/services/auth.service";
import { pendingRequestsInterceptor$ } from "ng-http-loader";
import { jwtInterceptor } from "./shared-core/interceptors/jwt.interceptor";
import { TopNavService } from "./shared/general/top-nav/services/top-nav.service";
import { TopNavAbstraction } from "./shared-core/abstractions/top-nav.abstraction";
import { ActiveDirectoryAbstraction } from "./shared-core/abstractions/active-directory.abstraction";
import { ActiveDirectoryService } from "./shared/company-hierarchy/directories/services/active-directory.service";
import { laraTheme } from "@shared-core/config/primeng";
import { MaintenanceBreakService } from "./shared/settings/system-settings/services/maintenance-break.service";
import { authInitializer } from "./shared/auth/initializers/auth.initializer";
import { SOCKET_ERROR_INTERCEPTOR } from "./shared/general/socket-io/tokens/socket-error-interceptor.token";
import { WsMaintenanceBreakErrorInterceptor } from "./shared/settings/system-settings/ws-interceptors/ws-maintenance-break-error.interceptor";
import { sentryUserContextInitializer } from "./shared-core/Initializers/sentry-user-context.Initializer";
import { sentryFeedbackInitializer } from "./shared-core/Initializers/sentry-feedback.Initializer";
import { sentryReportDialogInitializer } from "./shared-core/Initializers/sentry-report-dialog.Initializer";
import { provideFilePreview } from "./kernel/form/modules/file/file.config";
import * as Sentry from "@sentry/angular";
import localeIt from "@angular/common/locales/it";
import { provideEnabledApplications } from "./configs/applications/applications.config";
import { provideResourceChat } from "./shared/general/resource-chat/providers/provide-resource-chat";
import { VEHICLE_BLOCK_CHAT } from "./applications/movements-management/domains/vehicle-block/configs/vehicle-block-chat.config";
import { DAMAGE_CHAT } from "./applications/fleet/shared/damages/config/damage-chat.config";
import { eventManagersProviders } from "./kernel/plguins";

// Angular locale-data loaders, keyed by language code. Covers the queue kiosk
// languages (fr/de/es/ar/zh) on top of the app's own en/it so switching to any
// of them registers its locale data instead of crashing on a missing loader.
const locales: Record<string, () => Promise<{ default: unknown }>> = {
  en: () => import("@angular/common/locales/en"),
  it: () => import("@angular/common/locales/it"),
  fr: () => import("@angular/common/locales/fr"),
  de: () => import("@angular/common/locales/de"),
  es: () => import("@angular/common/locales/es"),
  ar: () => import("@angular/common/locales/ar"),
  zh: () => import("@angular/common/locales/zh"),
};
registerLocaleData(localeIt);
export const loadLocale = () => {
  const localStorage = inject(LocalStorageService);
  const stored = localStorage.get<string>("language") || ELocalizationLang.EN;
  // Fall back to English for any language we don't ship locale data for, so an
  // unknown stored code can never throw during bootstrap.
  const lang = locales[stored] ? stored : ELocalizationLang.EN;
  locales[lang]().then(d => registerLocaleData(d.default));
  return lang;
};

@Injectable()
class SentryErrorHandler implements ErrorHandler, OnDestroy {
  private sentryErrorHandler = Sentry.createErrorHandler();
  handleError(error: unknown): void {
    if (error === "Timeout") return;
    this.sentryErrorHandler.handleError(error);
  }
  ngOnDestroy(): void {
    this.sentryErrorHandler.ngOnDestroy();
  }
}

export const appConfig: ApplicationConfig = {
  providers: [
    ...eventManagersProviders,
    provideAppInitializer(() => authInitializer(inject(AuthService), inject(MaintenanceBreakService))),
    provideResourceChat(VEHICLE_BLOCK_CHAT, DAMAGE_CHAT),
    provideAppInitializer(() => {
      provideSideNav();
      return initializeTranslation(inject(TranslationService));
    }),
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideAnimationsAsync(),
    provideRouter(
      routes,
      withComponentInputBinding(),
      withRouterConfig({
        onSameUrlNavigation: "reload",
        paramsInheritanceStrategy: "always",
      })
    ),
    provideMessage(),
    provideEnabledApplications(),
    providePrimeNG({
      theme: {
        preset: laraTheme,
        options: {
          darkModeSelector: ".my-app-dark",
        },
      },
    }),
    {
      provide: LOCALE_ID,
      useFactory: loadLocale,
      deps: [LocalStorageService],
    },
    {
      provide: DEFAULT_CURRENCY_CODE,
      useValue: "EUR",
    },
    provideGraphQL(environment.graphql_endpoint),
    provideTranslation(),
    // Firebase setup
    ...provideFirebase(),
    provideConfirmationDialog(),
    importProvidersFrom(
      JwtModule.forRoot({
        config: { tokenGetter: _ => null },
      })
    ),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ErrorInterceptor,
      multi: true,
    },
    {
      provide: SOCKET_ERROR_INTERCEPTOR,
      useClass: WsMaintenanceBreakErrorInterceptor,
      multi: true,
    },
    {
      provide: BaseAuthService,
      useExisting: AuthService,
    },
    {
      provide: TopNavAbstraction,
      useExisting: TopNavService,
    },
    {
      provide: ActiveDirectoryAbstraction,
      useExisting: ActiveDirectoryService,
    },
    provideHttpClient(withInterceptors([jwtInterceptor]), withInterceptorsFromDi(), withInterceptors([pendingRequestsInterceptor$])),
    provideNotifications({ notificationApiImpl: NotificationApiService }),
    provideAlerts({ icon: "/icons/icon-144x144.ico" }),
    { provide: ErrorHandler, useValue: new SentryErrorHandler() },
    { provide: Sentry.TraceService, deps: [Router] },
    provideAppInitializer(() => {
      inject(Sentry.TraceService);
    }),
    provideAppInitializer(() => sentryUserContextInitializer()),
    provideAppInitializer(() => sentryFeedbackInitializer()),
    provideAppInitializer(() => sentryReportDialogInitializer()),
    provideFilePreview(),
  ],
};
